import * as classNames from 'classnames';
import React from 'react';
import { BASIC, EQUIPMENT } from '../lib/cardCategories.js';
import RULES from '../lib/rules.json';
import SetModePanel from './setModePanel';
import AnimatedBoard from './animatedBoard';
import './gameArea.css';
import {createSgsCard, createStsCard} from './cardSkin';

// Standard margin between objects
const DELTA = 10;

// Number of pixels between info objects inside the character card to the character card's border
const INFO_DELTA = 4;

// Ratio of ratio card to normal cards
const ROLE_RATIO = 0.25;

// Ratio of other player hand cards and equipment cards to normal cards
const CARD_RATIO = 0.3;

// Ratio of cards in the deck to normal cards
const DECK_RATIO = 0.5;

// Ratio of cards in the middle to normal cards
const MIDDLE_CARD_RATIO = 0.7;

const CARD_MODE = 'cardMode';

function get_next(currCardMode) {
    if (currCardMode === '' || currCardMode === 'sgs') return 'sts';
    return 'sgs';
};


export default class GameArea extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            mode: SetModePanel.DEFAULT_MODE,
            selectedIndex: undefined,
            helpCard: undefined,
            cardMode: window.localStorage.getItem(CARD_MODE),
        };
    }

    componentDidUpdate() {
        const { G, ctx, events, moves } = this.props;
        window.sanguosha = { G, ctx, events, moves };
    }

    render() {
        const { G, ctx, playerID, width, height, playerAreas, scaledWidth, scaledHeight } = this.props;
        const { characters } = G;
        const { numPlayers, playOrder } = ctx;

        const characterCards = [];
        const healthPoints = [];
        const normalCards = [];
        const nodes = [];

        const myPlayerIndex = Math.max(playOrder.indexOf(playerID), 0);
        playerAreas.forEach((playerArea, i) => {
            const playerIndex = (myPlayerIndex + i) % numPlayers;
            const player = playOrder[playerIndex];

            this.addPlayerName(playerArea, playerIndex, player, nodes);
            this.addCharacterRole(playerArea, playerIndex, nodes);

            const character = characters[player];
            this.addCharacterCard(playerArea, character, player, characterCards);
            if (!character) {
                return;
            }

            this.addHealth(playerArea, player, healthPoints, nodes);
            this.addChain(playerArea, player, nodes);
            this.addPlayerEquipment(playerArea, player, normalCards);
            if (player !== playerID) {
                this.addOtherPlayerHand(playerArea, player, normalCards, nodes);
                this.addOtherPlayerCharacterZone(playerArea, player, normalCards, nodes);
            }
        });

        this.addCharacterChoices(characterCards);

        this.addDeck(normalCards);
        this.addMyHand(normalCards);

        // Once cards of some type are found, remaining cards are rendered transparently.
        // We splice from the beginning so that these transparent cards don't block existing ones.
        const middleCards = [];
        let selfZoneCards = this.getPutOnSelfZoneCards();
        if(selfZoneCards.showing) {
            middleCards.splice(0, 0, ...selfZoneCards.cards);
        }
        middleCards.splice(0, 0, ...this.getPrivateZoneCards(selfZoneCards.showing || middleCards.length > 0));
        middleCards.splice(0, 0, ...this.getHarvestCards(selfZoneCards.showing || middleCards.length > 0));
        middleCards.splice(0, 0, ...this.getDiscardCards(selfZoneCards.showing || middleCards.length > 0));
        normalCards.push(...middleCards);

        return <div>
            <button
                className={classNames('card-mode', 'cycle')}
                onClick={() => {
                    this.setState({ cardMode: get_next(this.state.cardMode) });
                    window.localStorage.setItem(CARD_MODE, get_next(this.state.cardMode));
                }}
            />
            {this.renderMyArea()}
            <AnimatedBoard
                width={width}
                height={height}
                scaledWidth={scaledWidth}
                scaledHeight={scaledHeight}
                characterCards={characterCards}
                healthPoints={healthPoints}
                normalCards={normalCards}
                cardMode={this.state.cardMode}
            />
            {nodes}
            {this.renderActionButton()}
            {this.renderHelp()}
            {this.renderSetModePanel()}
        </div>;
    }

    addCharacterChoices(characterCards) {
        const { G, playerID, width, height, scaledWidth, scaledHeight } = this.props;
        const { mode, selectedIndex } = this.state;
        const { characterChoices } = G;
        const choices = characterChoices[playerID];
        if (this.stage() === 'selectCharacter' && choices !== undefined) {
            const startX = (width - choices.length * scaledWidth - (choices.length - 1) * DELTA) / 2;
            choices.forEach((choice, i) => {
                let onClick;
                if (mode === SetModePanel.DEFAULT_MODE) {
                    onClick = () => this.setState({ selectedIndex: i === selectedIndex ? undefined : i });
                } else if (mode === SetModePanel.HELP_MODE) {
                    onClick = () => this.setState({ helpCard: { key: choice.name, src: `/characters/${choice.name}.jpg` } });
                }
                characterCards.push({
                    key: `character-${choice.name}`,
                    name: choice.name,
                    faceUp: true,
                    opacity: 1,
                    left: startX + (scaledWidth + DELTA) * i,
                    top: (height - scaledHeight) / 2 - (i === selectedIndex ? 20 : 0),
                    width: scaledWidth,
                    height: scaledHeight,
                    onClick,
                });
            });
        }
    }

    addPlayerName(playerArea, playerIndex, player, nodes) {
        const { ctx, playerID, matchData, scaledWidth, scaledHeight } = this.props;
        const { currentPlayer } = ctx;
        if (matchData !== undefined && player !== playerID) {
            nodes.push(<div
                key={`name-${playerIndex}`}
                className={classNames('positioned player-name', { 'current-player': currentPlayer === player })}
                style={{
                    left: playerArea.x + INFO_DELTA,
                    top: playerArea.y + scaledHeight + INFO_DELTA,
                    width: scaledWidth - 2 * INFO_DELTA,
                    height: scaledHeight * 0.2,
                }}
            >
                {matchData[playerIndex].name}
            </div>);
        }
    }

    addCharacterRole(playerArea, playerIndex, nodes) {
        const { G, scaledWidth, scaledHeight } = this.props;
        const { roles } = G;
        const role = roles[playerIndex];
        const roleName = role.name || 'Role Back';
        nodes.push(<img
            key={`role-${role.id}`}
            className='positioned'
            src={`/roles/${roleName}.jpg`}
            alt={roleName}
            style={{
                left: playerArea.x + (1 - ROLE_RATIO) * scaledWidth - INFO_DELTA,
                top: playerArea.y + INFO_DELTA + scaledHeight * 0.05, // leave room for health bar
                width: scaledWidth * ROLE_RATIO,
                height: scaledHeight * ROLE_RATIO,
            }}
        />);
    }

    addCharacterCard(playerArea, character, player, characterCards) {
        const { G, moves, scaledWidth, scaledHeight } = this.props;
        const { mode, selectedIndex } = this.state;
        const { isAlive, isFlipped } = G;
        let onClick = undefined;
        if (mode === SetModePanel.GIVE_MODE && selectedIndex !== undefined) {
            onClick = () => {
                moves.give(selectedIndex, player);
                this.setState({ mode: SetModePanel.DEFAULT_MODE, selectedIndex: undefined });
            };
        } else if (mode === SetModePanel.GIVE_JUDGMENT_MODE) {
            onClick = () => {
                moves.play(selectedIndex, player);
                this.setState({ mode: SetModePanel.DEFAULT_MODE });
            };
        } else if (mode === SetModePanel.REVEAL_MODE && selectedIndex !== undefined) {
            onClick = () => {
                moves.reveal(selectedIndex, player);
                this.setState({ mode: SetModePanel.DEFAULT_MODE });
            };
        } else if (mode === SetModePanel.FLIP_MODE) {
            onClick = () => {
                moves.flipObject(player);
                this.setState({ mode: SetModePanel.DEFAULT_MODE });
            };
        } else if (mode === SetModePanel.HELP_MODE) {
            onClick = () => this.setState({ helpCard: { key: character.name, src: `/characters/${character.name}.jpg` } });
        } else if (mode === SetModePanel.COUNTRY_SCENE_MODE && selectedIndex !== undefined) {
            onClick = () => {
                moves.play(selectedIndex, player, 'Capture');
                this.setState({ mode: SetModePanel.DEFAULT_MODE });
            };
        } else if (mode === SetModePanel.BLOCKADE_MODE && selectedIndex !== undefined) {
            onClick = () => {
                moves.play(selectedIndex, player, 'Starvation');
                this.setState({ mode: SetModePanel.DEFAULT_MODE });
            };
        } else if (mode === SetModePanel.ALLIANCE_MODE) {
            if (selectedIndex === undefined) {
                onClick = () => this.setState({ selectedIndex: player });
            } else {
                onClick = () => {
                    moves.alliance(selectedIndex, player);
                    this.setState({ mode: SetModePanel.DEFAULT_MODE, selectedIndex: undefined });
                };
            }
        }
        characterCards.push({
            key: character ? `character-${character.name}` : `character-back-${player}`,
            name: character ? character.name : 'Character Back',
            faceUp: character !== undefined && !isFlipped[player],
            opacity: isAlive[player] ? 1 : 0.5,
            left: playerArea.x,
            top: playerArea.y,
            width: scaledWidth,
            height: scaledHeight,
            onClick,
        });
    }

    addHealth(playerArea, player, healthPoints, nodes) {
        const { G, moves, playerID, width, height, scaledWidth, scaledHeight } = this.props;
        const { characters, healths, isAlive, refusingDeath } = G;

        const isRefusingDeath = characters[player] && characters[player].name === 'Zhou Tai' && healths[player].current <= 0;
        const isDying = isRefusingDeath ? new Set(refusingDeath).size < refusingDeath.length : healths[player].current <= 0;

        for (let i = 0; i < (isRefusingDeath ? refusingDeath.length : healths[player].max); i++) {
            const color = !isRefusingDeath && i < healths[player].current ? 'green' : 'red';
            healthPoints.push({
                key: `health-${player}-${i}-${color}`,
                color,
                left: playerArea.x + scaledWidth * (0.23 + i * 0.06),
                top: playerArea.y + scaledHeight * 0.01,
                width: scaledWidth * 0.06,
                height: scaledHeight * 0.05,
            });
        }

        if (isAlive[player] && isDying) {
            const SAVE_ME_WIDTH = 100; // pixels
            const SAVE_ME_HEIGHT = 25; // pixels
            nodes.push(<button
                key='save-me'
                className='positioned bad'
                style={{
                    left: playerArea.x + (scaledWidth - SAVE_ME_WIDTH) / 2,
                    top: playerArea.y + (scaledHeight - SAVE_ME_HEIGHT) / 2,
                    width: SAVE_ME_WIDTH,
                    height: SAVE_ME_HEIGHT,
                }}
                disabled={true}
            >
                {'Save me!'}
            </button>);
        }

        if (player !== playerID) {
            return;
        }

        if (!isDying) {
            nodes.push(<div
                key='decrease-health'
                className='positioned image-div selectable decrease-health'
                style={{
                    left: playerArea.x + scaledWidth * 0.23,
                    top: playerArea.y + scaledHeight * 0.1,
                    width: scaledWidth * 0.12,
                    height: scaledHeight * 0.1,
                }}
                onClick={() => (isRefusingDeath ? moves.refusingDeath : moves.updateHealth)(-1)}
            />);
        } else if (isAlive[playerID]) {
            const DIE_BUTTON_WIDTH = 180;
            const DIE_BUTTON_HEIGHT = 30;
            nodes.push(<button
                key='die'
                className={`positioned selectable bad`}
                style={{
                    left: (width - DIE_BUTTON_WIDTH) / 2,
                    top: (height - DIE_BUTTON_HEIGHT) / 2,
                    width: DIE_BUTTON_WIDTH,
                    height: DIE_BUTTON_HEIGHT,
                }}
                onClick={() => moves.die()}
            >
                {'Die'}
            </button>);
        }
        if (healths[playerID].current < healths[playerID].max) {
            nodes.push(<div
                key='increase-health'
                className='positioned image-div selectable increase-health'
                style={{
                    left: playerArea.x + scaledWidth * 0.39,
                    top: playerArea.y + scaledHeight * 0.1,
                    width: scaledWidth * 0.12,
                    height: scaledHeight * 0.1,
                }}
                onClick={() => (isRefusingDeath ? moves.refusingDeath : moves.updateHealth)(+1)}
            />);
        }

        if (healths[playerID].max > 1) {
            nodes.push(<div
                key='decrease-max-health'
                className='positioned image-div selectable decrease-max-health'
                style={{
                    // left: playerArea.x + scaledWidth * 0.265,
                    // top: playerArea.y + scaledHeight * 0.2,
                    // width: scaledWidth * 0.09,
                    // height: scaledHeight * 0.075,
                    left: playerArea.x + scaledWidth * (0.23 - 1.25*0.06),
                    top: playerArea.y + scaledHeight * 0.01,
                    width: scaledWidth * 0.06,
                    height: scaledHeight * 0.05,
                }}
                onClick={() => (moves.updateMaxHealth)(-1)}
            />);
        }

        if (healths[playerID].max < 10) {
            nodes.push(<div
                key='increase-max-health'
                className='positioned image-div selectable increase-max-health'
                style={{
                    // left: playerArea.x + scaledWidth * 0.375,
                    // top: playerArea.y + scaledHeight * 0.2,
                    // width: scaledWidth * 0.09,
                    // height: scaledHeight * 0.075,
                    left: playerArea.x + scaledWidth * (0.23 + 10.25 * 0.06),
                    top: playerArea.y + scaledHeight * 0.01,
                    width: scaledWidth * 0.06,
                    height: scaledHeight * 0.05,
                }}
                onClick={() => (moves.updateMaxHealth)(1)}
            />);
        }
    }

    addChain(playerArea, player, nodes) {
        const { G, moves, playerID, scaledWidth, scaledHeight } = this.props;
        const { isChained } = G;
        let onClick = undefined;
        if (player === playerID) {
            onClick = () => moves.toggleChain();
        }
        if (player === playerID || isChained[player]) {
            nodes.push(<div
                key={`chain-${player}`}
                className={classNames('positioned image-div chain', { 'gray': !isChained[player] }, { 'selectable': onClick !== undefined })}
                style={{
                    left: playerArea.x + (1 - ROLE_RATIO) * scaledWidth - 2 * INFO_DELTA,
                    top: playerArea.y + scaledHeight * 0.25,
                    width: scaledWidth * ROLE_RATIO + 2 * INFO_DELTA,
                    height: scaledHeight * 0.16,
            }}
                onClick={onClick}
            />);
        }
    }

    addPlayerEquipment(playerArea, player, normalCards) {
        const { G, moves, playerID, scaledWidth, scaledHeight } = this.props;
        const { mode } = this.state;
        const { equipment, isFlipped } = G;
        ['Weapon', 'Shield', '+1', '-1', 'Lightning', 'Capture', 'Starvation'].forEach((category, i) => {
            const card = equipment[player][category];
            if (card) {
                let onClick = undefined;
                if ((mode === SetModePanel.DEFAULT_MODE && player === playerID)
                    || mode === SetModePanel.DISMANTLE_MODE
                    || mode === SetModePanel.STEAL_MODE) {
                    onClick = () => {
                        (mode === SetModePanel.DEFAULT_MODE || mode === SetModePanel.DISMANTLE_MODE ? moves.dismantle : moves.steal)({
                            playerID: player,
                            category,
                        });
                        this.setState({ mode: SetModePanel.DEFAULT_MODE });
                    };
                } else if (mode === SetModePanel.HELP_MODE) {
                    onClick = () => this.setState({ helpCard: { key: card.type, card: card } });
                }
                if (i < 4) {
                    // Equipment cards
                    normalCards.push({
                        key: `card-${card.id}`,
                        className: 'small-shadow',
                        card,
                        faceUp: !isFlipped[card.id],
                        opacity: 1,
                        left: playerArea.x + (scaledWidth - (CARD_RATIO * scaledWidth + INFO_DELTA) * (2 - i % 2)),
                        top: playerArea.y + (scaledHeight - (CARD_RATIO * scaledHeight + INFO_DELTA) * (2 - Math.floor(i / 2))),
                        scale: CARD_RATIO,
                        alwaysDown: false,
                        onClick,
                    });
                } else {
                    // Judgment cards
                    normalCards.push({
                        key: `card-${card.id}`,
                        className: 'small-shadow',
                        card,
                        faceUp: true,
                        sideways: true,
                        opacity: 1,
                        left: playerArea.x + scaledWidth * 0.33,
                        top: playerArea.y + scaledHeight * (0.16 + 0.18 * (i - 4)),
                        scale: CARD_RATIO,
                        alwaysDown: false,
                        onClick,
                    });
                }
            }
        });
    }

    addOtherPlayerHand(playerArea, player, normalCards, nodes) {
        const { G, moves, scaledWidth, scaledHeight } = this.props;
        const { mode } = this.state;
        const { hands } = G;
        const hand = hands[player];
        // Show the card backs
        hand.forEach(card => {
            let onClick = undefined;
            if (mode === SetModePanel.DISMANTLE_MODE || mode === SetModePanel.STEAL_MODE) {
                onClick = () => {
                    (mode === SetModePanel.DISMANTLE_MODE ? moves.dismantle : moves.steal)({
                        playerID: player,
                        index: Math.floor(Math.random() * hand.length),
                    });
                    this.setState({ mode: SetModePanel.DEFAULT_MODE });
                };
            }
            normalCards.push({
                key: `card-${card.id}`,
                className: 'small-shadow',
                card,
                opacity: 1,
                left: playerArea.x + INFO_DELTA,
                top: playerArea.y + (1 - CARD_RATIO) * scaledHeight - INFO_DELTA,
                scale: CARD_RATIO,
                alwaysDown: true,
                onClick,
            });
        });
        // Show the card count
        if (hand.length > 0) {
            nodes.push(<div
                key={`card-count-${player}`}
                className='game-label'
                style={{
                    left: playerArea.x + INFO_DELTA,
                    top: playerArea.y + (1 - CARD_RATIO) * scaledHeight - INFO_DELTA,
                    width: scaledWidth * CARD_RATIO,
                    height: scaledHeight * CARD_RATIO,
                    marginLeft: scaledWidth * CARD_RATIO * 0.1,
                    marginTop: scaledWidth * CARD_RATIO * 0.1,
                    fontSize: scaledWidth * CARD_RATIO * 0.6,
                }}
            >
                {hand.length}
            </div>);
        }
    }

    addOtherPlayerCharacterZone(playerArea, player, normalCards, nodes) {
        const { G, scaledWidth, scaledHeight } = this.props;
        const { putOnCharacterZone } = G;
        const hand = putOnCharacterZone.filter(item => item.visibleTo.includes(player));
        // Show the card backs
        hand.forEach(card => {
            let onClick = undefined;
            normalCards.push({
                key: `card-${card.id}`,
                className: 'small-shadow',
                card,
                opacity: 1,
                left: playerArea.x + INFO_DELTA + scaledWidth * 0.22,
                top: playerArea.y + (1 - CARD_RATIO * 0.5) * scaledHeight - INFO_DELTA,
                scale: CARD_RATIO * 0.5,
                alwaysDown: true,
                onClick,
            });
        });
        // Show the card count
        if (hand.length > 0) {
            nodes.push(<div
                key={`card-count-character-zone-${player}`}
                className='game-label'
                style={{
                    left: playerArea.x + INFO_DELTA + scaledWidth * 0.22,
                    top: playerArea.y + (1 - CARD_RATIO * 0.5) * scaledHeight - INFO_DELTA,
                    width: scaledWidth * CARD_RATIO * 0.5,
                    height: scaledHeight * CARD_RATIO * 0.5,
                    marginLeft: scaledWidth * CARD_RATIO * 0.1,
                    marginTop: scaledWidth * CARD_RATIO * 0.1,
                    fontSize: scaledWidth * CARD_RATIO * 0.3,
                }}
            >
                {hand.length}
            </div>);
        }
    }

    addDeck(normalCards) {
        const { G, moves, height, scaledHeight } = this.props;
        const { mode } = this.state;
        const { deck, privateZone } = G;
        const MAX_CARDS_SHOWN = 10;
        deck.slice(-MAX_CARDS_SHOWN).forEach((card, i) => {
            let onClick = undefined;
            if (mode === SetModePanel.DEFAULT_MODE && card === deck[deck.length - 1]) {
                const doingAstrology = privateZone.filter(item => item.source.deck).length > 0;
                if (doingAstrology) {
                    onClick = () => moves.astrology(1);
                } else {
                    onClick = () => moves.draw();
                }
            }
            normalCards.push({
                key: `card-${card.id}`,
                card,
                opacity: 1,
                left: DELTA * (1 - i / MAX_CARDS_SHOWN),
                top: height - scaledHeight * DECK_RATIO - DELTA * (i / MAX_CARDS_SHOWN),
                scale: DECK_RATIO,
                alwaysDown: true,
                onClick,
            });
        });
    }

    addMyHand(normalCards) {
        const { G, playerID, width, height, scaledWidth, scaledHeight } = this.props;
        const { hands, isFlipped } = G;
        const myHand = hands[playerID];
        if (myHand) {
            const spacing = Math.min(scaledWidth + DELTA, (width - (2 + DECK_RATIO) * scaledWidth - 5 * DELTA) / (hands[playerID].length - 1));
            hands[playerID].forEach((card, i) => {
                const onClick = this.selectFunction(i);
                normalCards.push({
                    key: `card-${card.id}`,
                    card,
                    faceUp: !isFlipped[card.id],
                    opacity: onClick !== undefined ? 1 : 0.3,
                    left: DECK_RATIO * scaledWidth + 2 * DELTA + spacing * i,
                    top: height - scaledHeight - DELTA,
                    scale: 1,
                    alwaysDown: false,
                    onClick,
                });
            })
        }
    }

    getPrivateZoneCards(middleCardsFound) {
        const { G, moves, playerID, width, height, scaledWidth, scaledHeight } = this.props;
        const { mode } = this.state;
        const { privateZone } = G;
        const privateCards = privateZone.filter(item => item.visibleTo.includes(playerID));
        const startX = (width - privateCards.length * scaledWidth * MIDDLE_CARD_RATIO - (privateCards.length - 1) * DELTA) / 2;
        const normalCards = [];
        privateCards.forEach(({ card, visibleTo }, i) => {
            let onClick = undefined;
            if (mode === SetModePanel.DEFAULT_MODE) {
                onClick = () => moves.returnCard(card.id);
            } else if (mode === SetModePanel.HELP_MODE) {
                onClick = () => this.setState({ helpCard: { key: card.type, card: card } });
            }
            normalCards.push({
                key: `card-${card.id}`,
                className: 'shadow',
                card,
                faceUp: true,
                opacity: middleCardsFound ? 0 : 1,
                left: startX + (scaledWidth * MIDDLE_CARD_RATIO + DELTA) * i,
                top: (height - scaledHeight * MIDDLE_CARD_RATIO) / 2,
                scale: MIDDLE_CARD_RATIO,
                alwaysDown: false,
                onClick: middleCardsFound ? undefined : onClick,
            });
        });
        return normalCards;
    }

    getPutOnSelfZoneCards(middleCardsFound) {
        const { G, moves, playerID, width, height, scaledWidth, scaledHeight } = this.props;
        const { mode } = this.state;
        const { putOnCharacterZone, isCharacterZoneOpen } = G;

        const privateCards = putOnCharacterZone.filter(item => item.visibleTo.includes(playerID));
        const startX = (width - privateCards.length * scaledWidth * MIDDLE_CARD_RATIO - (privateCards.length - 1) * DELTA) / 2;
        const normalCards = [];

        privateCards.forEach(({ card, visibleTo }, i) => {
            let onClick = undefined;
            if (mode === SetModePanel.DEFAULT_MODE) {
                onClick = () => moves.pickUpCharacter(card.id);
            } else if (mode === SetModePanel.HELP_MODE) {
                onClick = () => this.setState({ helpCard: { key: card.type, card: card } });
            }
            normalCards.push({
                key: `card-${card.id}`,
                className: 'shadow',
                card,
                faceUp: true,
                opacity: middleCardsFound ? 0 : 1,
                left: startX + (scaledWidth * MIDDLE_CARD_RATIO + DELTA) * i,
                top: (height - scaledHeight * MIDDLE_CARD_RATIO) / 2,
                scale: MIDDLE_CARD_RATIO,
                alwaysDown: false,
                onClick: middleCardsFound ? undefined : onClick,
            });
        });
        return {
            cards: normalCards,
            showing: !!isCharacterZoneOpen[playerID]
        };
    }

    getHarvestCards(middleCardsFound) {
        const { G, moves, width, height, scaledWidth, scaledHeight } = this.props;
        const { mode } = this.state;
        const { harvest } = G;
        const startX = (width - harvest.length * scaledWidth * MIDDLE_CARD_RATIO - (harvest.length - 1) * DELTA) / 2;
        const normalCards = [];
        harvest.forEach((card, i) => {
            let onClick = undefined;
            if (mode === SetModePanel.DEFAULT_MODE) {
                onClick = () => moves.pickUpHarvest(i);
            } else if (mode === SetModePanel.HELP_MODE) {
                onClick = () => this.setState({ helpCard: { key: card.type, card: card } });
            }
            normalCards.push({
                key: `card-${card.id}`,
                className: 'shadow',
                card,
                faceUp: true,
                opacity: middleCardsFound ? 0 : 1,
                left: startX + (scaledWidth * MIDDLE_CARD_RATIO + DELTA) * i,
                top: (height - scaledHeight * MIDDLE_CARD_RATIO) / 2,
                scale: MIDDLE_CARD_RATIO,
                alwaysDown: false,
                onClick: middleCardsFound ? undefined : onClick,
            });
        });
        return normalCards;
    }

    getDiscardCards(middleCardsFound) {
        const { G, moves, width, height, scaledWidth, scaledHeight } = this.props;
        const { mode } = this.state;
        const { discard, isFlipped } = G;
        const MAX_DISCARDS_SHOWN = 4;
        const numCardsShown = Math.min(discard.length, MAX_DISCARDS_SHOWN);
        const startX = (width - numCardsShown * scaledWidth * MIDDLE_CARD_RATIO - (numCardsShown - 1) * DELTA) / 2;
        const normalCards = [];
        for (let i = 0; i < discard.length && i <= MAX_DISCARDS_SHOWN; i++) {
            const card = discard[discard.length - 1 - i];
            let onClick = undefined;
            if (mode === SetModePanel.DEFAULT_MODE && i < MAX_DISCARDS_SHOWN) {
                onClick = () => moves.pickUp(discard.length - 1 - i);
            } else if (mode === SetModePanel.FLIP_MODE) {
                onClick = () => {
                    moves.flipObject(card.id);
                    this.setState({ mode: SetModePanel.DEFAULT_MODE });
                };
            } else if (mode === SetModePanel.HELP_MODE) {
                onClick = () => this.setState({ helpCard: { key: card.type, card: card } });
            }
            normalCards.push({
                key: `card-${card.id}`,
                className: 'shadow',
                card,
                faceUp: !isFlipped[card.id],
                opacity: i === MAX_DISCARDS_SHOWN || middleCardsFound ? 0 : 1,
                left: startX + (scaledWidth * MIDDLE_CARD_RATIO + DELTA) * i,
                top: (height - scaledHeight * MIDDLE_CARD_RATIO) / 2,
                scale: MIDDLE_CARD_RATIO,
                alwaysDown: false,
                onClick: middleCardsFound ? undefined : onClick,
            });
        }
        return normalCards;
    }

    renderSetModePanel() {
        const { G, ctx, moves, playerID } = this.props;
        const { mode } = this.state;
        return <SetModePanel
            key='set-mode-panel'
            G={G}
            ctx={ctx}
            moves={moves}
            playerID={playerID}
            mode={mode}
            setMode={mode => this.setState({ mode, selectedIndex: undefined, helpCard: undefined })}
            setSelectedIndex={selectedIndex => this.setState({ selectedIndex })}
            selectFunction={this.selectFunction}
        />;
    }

    renderMyArea() {
        const { scaledHeight } = this.props;
        return <div
            key='my-area'
            className='my-area'
            style={{
                height: scaledHeight + 2 * DELTA,
            }}
        />;
    }

    renderActionButton() {
        const { G, ctx, moves, playerID, width, height, scaledHeight, playAgain } = this.props;
        const { mode, selectedIndex } = this.state;
        const { isAlive, privateZone } = G;
        const { currentPlayer, gameover, phase, activePlayers } = ctx;
        const ACTION_BUTTON_WIDTH = 160;
        const ACTION_BUTTON_HEIGHT = 30;
        let actionButton = undefined;
        if (gameover && playAgain) {
            actionButton = {
                text: 'Play again',
                type: 'selectable warn',
                onClick: playAgain,
            };
        } else if (this.stage() === 'selectCharacter' && selectedIndex !== undefined) {
            actionButton = {
                text: 'Select',
                type: 'selectable warn',
                onClick: () => {
                    moves.selectCharacter(selectedIndex);
                    this.setState({ selectedIndex: undefined });
                },
            };
        } else if (phase === 'selectCharacters' && this.stage() === undefined) {
            actionButton = {
                text: activePlayers[currentPlayer] === 'selectCharacter' ? 'Waiting for king...' : 'Waiting for others...',
                type: 'disabled',
            };
        } else if (!isAlive[playerID]) {
            return undefined;
        } else if ((mode === SetModePanel.GIVE_MODE && selectedIndex !== undefined)
            || (mode === SetModePanel.REVEAL_MODE && selectedIndex !== undefined)
            || mode === SetModePanel.GIVE_JUDGMENT_MODE
            || (mode === SetModePanel.COUNTRY_SCENE_MODE && selectedIndex !== undefined)
            || (mode === SetModePanel.BLOCKADE_MODE && selectedIndex !== undefined)
            || (mode === SetModePanel.ALLIANCE_MODE && selectedIndex === undefined)) {
            actionButton = {
                text: 'Select player',
                type: 'disabled',
            };
        } else if (mode === SetModePanel.ALLIANCE_MODE && selectedIndex !== undefined) {
            actionButton = {
                text: 'Select player 2',
                type: 'disabled',
            };
        } else if (this.stage() === 'play' && currentPlayer === playerID && privateZone.length === 0) {
            actionButton = {
                text: 'End play',
                type: 'selectable warn',
                onClick: () => moves.endPlay(),
            }
        } else if (this.stage() === 'discard') {
            actionButton = {
                text: 'Discard cards',
                type: 'disabled',
            };
        }
        if (actionButton !== undefined) {
            const { text, type, onClick } = actionButton;
            return <button
                className={`positioned ${type}`}
                style={{
                    left: (width - ACTION_BUTTON_WIDTH) / 2,
                    top: height - scaledHeight - ACTION_BUTTON_HEIGHT - 3 * DELTA,
                    width: ACTION_BUTTON_WIDTH,
                    height: ACTION_BUTTON_HEIGHT,
                }}
                onClick={onClick}
                disabled={onClick === undefined}
            >
                {text}
            </button>;
        }
    }

    renderHelp() {
        const { mode, helpCard } = this.state;
        var imgDiv
        if (mode === SetModePanel.HELP_MODE && helpCard !== undefined) {
            if (helpCard.src !== undefined) {
                imgDiv = <img className='help-panel-img' src={helpCard.src} alt='card' />;
            }
            else {
                imgDiv= <div className='help-panel-card'>{this.state.cardMode==='sgs' ? createSgsCard(helpCard.card) : createStsCard(helpCard.card)}</div>;
            }
            return <div
                className='help-panel'
            >
                {imgDiv}
                <div className='help-panel-div' dangerouslySetInnerHTML={{ __html: RULES[helpCard.key] }} />
                <button
                    className='selectable bad'
                    onClick={() => this.setState({ mode: SetModePanel.DEFAULT_MODE, helpCard: undefined })}
                >
                    {'X'}
                </button>
            </div>;
        }
    }

    selectFunction = index => {
        const { G, moves, playerID } = this.props;
        const { mode, selectedIndex } = this.state;
        const { hands, harvest, isCharacterZoneOpen } = G;
        const card = hands[playerID][index];
        if (mode === SetModePanel.DEFAULT_MODE && this.stage() === 'play') {
            if (isCharacterZoneOpen[playerID]) {
                return () => moves.putOnCharacter(index);
            } else if (harvest.length > 0) {
                return () => moves.putDownHarvest(index);
            } else if (['Capture', 'Starvation'].includes(EQUIPMENT[card.type])) {
                return () => this.setState({ mode: SetModePanel.GIVE_JUDGMENT_MODE, selectedIndex: index });
            } else {
                return () => moves.play(index);
            }
        } else if (mode === SetModePanel.DEFAULT_MODE && this.stage() === 'discard') {
            return () => moves.discardCard(index);
        } else if (mode === SetModePanel.GIVE_MODE && selectedIndex === undefined) {
            return () => this.setState({ selectedIndex: index });
        } else if (mode === SetModePanel.DISMANTLE_MODE) {
            return () => {
                moves.dismantle({
                    playerID,
                    index: index,
                });
                this.setState({ mode: SetModePanel.DEFAULT_MODE });
            };
        } else if (mode === SetModePanel.REVEAL_MODE && selectedIndex === undefined) {
            return () => this.setState({ selectedIndex: index });
        } else if (mode === SetModePanel.FLIP_MODE) {
            return () => {
                moves.flipObject(card.id);
                this.setState({ mode: SetModePanel.DEFAULT_MODE });
            };
        } else if (mode === SetModePanel.HELP_MODE) {
            return () => this.setState({ helpCard: { key: card.type, card: card } });
        } else if (mode === SetModePanel.COUNTRY_SCENE_MODE && selectedIndex === undefined) {
            if (card.suit === 'DIAMOND') {
                return () => this.setState({ mode: SetModePanel.COUNTRY_SCENE_MODE, selectedIndex: index });
            }
        } else if (mode === SetModePanel.BLOCKADE_MODE && selectedIndex === undefined) {
            if (['CLUB', 'SPADE'].includes(card.suit) && (BASIC.includes(card.type) || EQUIPMENT[card.type])) {
                return () => this.setState({ mode: SetModePanel.COUNTRY_SCENE_MODE, selectedIndex: index });
            }
        }
    }

    stage() {
        const { ctx, playerID } = this.props;
        const { activePlayers } = ctx;
        return activePlayers && activePlayers[playerID];
    }
}
